import { createInvocationId, logTemplateWebMCP } from '../logging';
import { captureTemplatePreview } from '../previewCaptureRegistry';
import { mcpToolResult, toolError, toolSuccess, type TemplateWebMCPToolResult } from '../responses';
import { sanitizeEditorSummary, type TemplateBuilderReadElement } from '../sanitize';
import type { CatalogState } from '../liveCatalog';
import type { TemplateToolContext } from '../toolContext';

const READ_SOURCE_SCHEMA = {
  type: 'object',
  properties: {
    sourceId: { type: 'string' },
    name: { type: 'string' },
  },
  required: ['sourceId', 'name'],
} as const;

const READ_NAME_CONFIG_SCHEMA = {
  type: 'object',
  properties: {
    isEditable: { type: 'boolean' },
    customAsset: {
      anyOf: [
        {
          type: 'object',
          properties: {
            folder: { type: 'string' },
            fileTemplate: { type: 'string' },
          },
          required: ['folder', 'fileTemplate'],
        },
        { type: 'null' },
      ],
    },
  },
  required: ['isEditable'],
} as const;

const READ_DYNAMIC_FONT_SCHEMA = {
  type: 'object',
  properties: {
    enabled: { type: 'boolean' },
    maxFontSize: { type: 'number' },
    minFontSize: { type: 'number' },
    lineHeight: { type: 'number' },
    multiLine: { type: 'boolean' },
  },
  required: ['enabled'],
} as const;

const READ_COLOR_SCHEMA = {
  type: 'object',
  properties: {
    r: { type: 'number' },
    g: { type: 'number' },
    b: { type: 'number' },
    a: { type: 'number' },
  },
  required: ['r', 'g', 'b', 'a'],
} as const;

const READ_STYLE_SCHEMA = {
  type: 'object',
  properties: {
    textAlign: { type: ['string', 'null'] },
    textAlignVertical: { type: ['string', 'null'] },
    fontSize: { type: ['number', 'null'] },
    lineHeight: { type: ['number', 'null'] },
    letterSpacing: { type: ['number', 'null'] },
    fontFamily: { type: ['string', 'null'] },
    fontVariant: { type: ['string', 'null'] },
    fontWeight: { type: ['number', 'null'] },
    fontStyle: { type: ['string', 'null'] },
    fontStretch: { type: ['string', 'null'] },
    textCase: { type: ['string', 'null'] },
    color: { type: ['string', 'null'] },
    stroke: { type: ['string', 'null'] },
    strokeWeight: { type: ['number', 'null'] },
    fills: {
      anyOf: [
        {
          type: 'array',
          items: {
            type: 'object',
            properties: {
              type: { type: 'string' },
              color: { anyOf: [READ_COLOR_SCHEMA, { type: 'null' }] },
              opacity: { type: ['number', 'null'] },
              gradientStops: { anyOf: [{ type: 'array' }, { type: 'null' }] },
              gradientHandlePositions: { anyOf: [{ type: 'array' }, { type: 'null' }] },
            },
            required: ['type'],
          },
        },
        { type: 'null' },
      ],
    },
  },
} as const;

const READ_ELEMENT_SCHEMA = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    type: { type: 'string' },
    name: { type: 'string' },
    position: {
      type: 'object',
      properties: { x: { type: 'number' }, y: { type: 'number' }, z: { type: 'number' } },
      required: ['x', 'y', 'z'],
    },
    size: {
      type: 'object',
      properties: { width: { type: 'number' }, height: { type: 'number' } },
      required: ['width', 'height'],
    },
    rotation: { type: ['number', 'null'] },
    hidden: { type: 'boolean' },
    locked: { type: 'boolean' },
    sources: { type: 'array', items: READ_SOURCE_SCHEMA },
    nameConfig: { anyOf: [READ_NAME_CONFIG_SCHEMA, { type: 'null' }] },
    contentTokens: { type: 'array', items: { type: 'string' } },
    contentTemplate: { type: ['string', 'null'] },
    dynamicFont: { anyOf: [READ_DYNAMIC_FONT_SCHEMA, { type: 'null' }] },
    style: { anyOf: [READ_STYLE_SCHEMA, { type: 'null' }] },
  },
  required: [
    'id',
    'type',
    'name',
    'position',
    'size',
    'rotation',
    'hidden',
    'locked',
    'sources',
    'contentTokens',
  ],
} as const;

export const TEMPLATE_BUILDER_READ_TOOL = {
  name: 'template_builder_read',
  // Listings over the relay only expose description (and annotations). The output shape lives
  // here rather than on `annotations`, which is restricted to a short title and boolean hints.
  description:
    'Read the open Template Builder document or a live catalog. {} and omitted readType mean document. readType is document, selection, element, source_options, market_options, data_points, advertisers, brand, or preview. preview args are { format?: jpeg|png, quality?: number } (default jpeg). preview returns workingRevision, width, and height in data (CSS pixels, pixel ratio 1) and attaches the raster only in content (not in structuredContent or logs). preview returns PREVIEW_FAILED if capture exceeds 15 seconds. data_points args are { elementType?, elementId? }. advertisers lists advertisers on create and duplicate, and only the current id on edit. brand holds fontFamilies and fonts with per-family variants (label, fontVariant, fontWeight, fontStyle, fontStretch). Returns { ok, data?, error? }. Document data holds readType, templateId, name, bgColour, selectedElementId, elementCount, workingRevision, dataSources ({ id, type }) and elements. Each element holds id, type, name, position ({ x, y, z }), size ({ width, height }), rotation, hidden, locked, sources ({ sourceId, name }), contentTokens, and the nullable nameConfig, contentTemplate, dynamicFont and style. IMAGE contentTemplate is the full URL when it contains dynamic brace tokens; static image URLs are omitted. Text style holds textAlign, textAlignVertical, fontSize, lineHeight, letterSpacing, fontFamily, fontVariant, fontWeight, fontStyle, fontStretch, textCase, color, stroke, strokeWeight, and fills (type, color, opacity, gradient stops). The first SVG element size is the stage the preview uses. Error code is UNAUTHORIZED, INVALID_INPUT, EDITOR_NOT_READY, PREVIEW_FAILED or UNEXPECTED.',
  enabled: true,
  annotations: {
    title: 'Read Template Builder',
    readOnlyHint: true,
    destructiveHint: false,
    idempotentHint: true,
    openWorldHint: false,
  },
  inputSchema: {
    type: 'object',
    properties: {
      readType: {
        type: 'string',
        enum: [
          'document',
          'selection',
          'element',
          'source_options',
          'market_options',
          'data_points',
          'advertisers',
          'brand',
          'preview',
        ],
      },
      args: { type: 'object' },
    },
    additionalProperties: false,
  } as const,
  outputSchema: {
    type: 'object',
    properties: {
      ok: { type: 'boolean' },
      data: {
        type: 'object',
        properties: {
          readType: { type: 'string' },
          workingRevision: { type: 'integer' },
          templateId: { type: 'string' },
          name: { type: 'string' },
          bgColour: { type: 'string' },
          selectedElementId: { type: ['string', 'null'] },
          elementCount: { type: 'integer' },
          dataSources: {
            type: 'array',
            items: {
              type: 'object',
              properties: { id: { type: 'string' }, type: { type: 'string' } },
              required: ['id', 'type'],
            },
          },
          elements: { type: 'array', items: READ_ELEMENT_SCHEMA },
          element: { anyOf: [READ_ELEMENT_SCHEMA, { type: 'null' }] },
          elementType: { type: 'string' },
          sources: { type: 'array' },
          sourceId: { type: ['string', 'null'] },
          templateType: { type: ['string', 'null'] },
          markets: { type: 'array' },
          stageMarkets: { type: 'array' },
          dataPoints: { type: 'array' },
          advertisers: { type: 'array' },
          fontFamilies: { type: 'array', items: { type: 'string' } },
          fonts: { type: 'array' },
          brandKitId: { type: ['string', 'null'] },
          brandKitName: { type: ['string', 'null'] },
          colors: { type: 'array' },
          colorCount: { type: 'integer' },
          logoPresent: { type: 'boolean' },
          width: { type: 'integer' },
          height: { type: 'integer' },
          format: { type: 'string' },
        },
        required: ['readType', 'workingRevision'],
      },
      error: {
        type: 'object',
        properties: { code: { type: 'string' }, message: { type: 'string' } },
        required: ['code', 'message'],
      },
    },
    required: ['ok'],
  } as const,
};

const READ_TYPES = new Set([
  'document',
  'selection',
  'element',
  'source_options',
  'market_options',
  'data_points',
  'advertisers',
  'brand',
  'preview',
]);

export const PREVIEW_CAPTURE_TIMEOUT_MS = 15_000;

class PreviewCaptureTimeoutError extends Error {
  constructor() {
    super(`Preview capture timed out after ${PREVIEW_CAPTURE_TIMEOUT_MS / 1000}s.`);
    this.name = 'PreviewCaptureTimeoutError';
  }
}

/**
 * html-to-image fetches images and fonts with no deadline. Race the capture
 * against this timeout and the tool abort signal so a hung asset still
 * finishes the invocation.
 */
async function withPreviewCaptureDeadline<T>(
  work: (signal: AbortSignal) => Promise<T>,
  parentSignal?: AbortSignal,
): Promise<T> {
  const controller = new AbortController();
  const aborted = new Promise<never>((_, reject) => {
    controller.signal.addEventListener('abort', () => reject(controller.signal.reason), {
      once: true,
    });
  });

  const timer = setTimeout(() => {
    controller.abort(new PreviewCaptureTimeoutError());
  }, PREVIEW_CAPTURE_TIMEOUT_MS);

  const onParentAbort = () => {
    controller.abort(new DOMException('The tool invocation was cancelled.', 'AbortError'));
  };
  if (parentSignal?.aborted) {
    onParentAbort();
  } else {
    parentSignal?.addEventListener('abort', onParentAbort, { once: true });
  }

  const capture = work(controller.signal);
  // The deadline can win while html-to-image is still in flight. Swallow that
  // later rejection so it does not surface as an unhandled promise.
  void capture.catch(() => undefined);

  try {
    return await Promise.race([capture, aborted]);
  } catch (error) {
    if (controller.signal.reason instanceof PreviewCaptureTimeoutError) {
      throw controller.signal.reason;
    }
    if (parentSignal?.aborted) {
      throw new DOMException('The tool invocation was cancelled.', 'AbortError');
    }
    throw error;
  } finally {
    clearTimeout(timer);
    parentSignal?.removeEventListener('abort', onParentAbort);
  }
}

function parsePreviewArgs(
  args: Record<string, unknown>,
): { format: 'jpeg' | 'png'; quality: number } | { error: string } {
  const formatRaw = args.format;
  const format =
    formatRaw === undefined || formatRaw === 'jpeg' ? 'jpeg' : formatRaw === 'png' ? 'png' : null;
  if (format == null) {
    return { error: 'preview args.format must be jpeg or png.' };
  }
  if (args.quality === undefined) {
    return { format, quality: 0.85 };
  }
  if (typeof args.quality !== 'number' || !Number.isFinite(args.quality)) {
    return { error: 'preview args.quality must be a number.' };
  }
  if (args.quality < 0.1 || args.quality > 1) {
    return { error: 'preview args.quality must be between 0.1 and 1.' };
  }
  return { format, quality: args.quality };
}

type ExecuteArgs = TemplateToolContext & {
  persist?: (...args: unknown[]) => unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function parseReadInput(
  input: unknown,
): { readType: string; args: Record<string, unknown> } | { error: string } {
  if (input == null) {
    return { readType: 'document', args: {} };
  }
  if (!isRecord(input)) {
    return { error: 'template_builder_read expects an object.' };
  }
  const keys = Object.keys(input).filter((key) => key !== 'readType' && key !== 'args');
  if (keys.length > 0) {
    return { error: 'template_builder_read only accepts readType and args.' };
  }
  const readType = input.readType == null ? 'document' : input.readType;
  if (typeof readType !== 'string' || !READ_TYPES.has(readType)) {
    return { error: 'Unknown readType.' };
  }
  if (input.args != null && !isRecord(input.args)) {
    return { error: 'args must be an object.' };
  }
  return { readType, args: isRecord(input.args) ? input.args : {} };
}

function elementSvg(state: { elements?: unknown }, element: TemplateBuilderReadElement) {
  if (element.type !== 'SVG' || !Array.isArray(state.elements)) {
    return element;
  }
  const raw = state.elements.find(
    (candidate) => isRecord(candidate) && candidate.id === element.id,
  ) as { data?: { content?: unknown } } | undefined;
  const content = raw?.data?.content;
  if (typeof content !== 'string' || content.startsWith('http') || content.startsWith('data:')) {
    return element;
  }
  if (!content.includes('<svg')) {
    return element;
  }
  return { ...element, svg: content };
}

export async function executeTemplateBuilderRead({
  store,
  templateId,
  canEdit,
  input,
  signal,
  persist: _persist,
  routeMode = 'edit',
  listAdvertisers,
}: ExecuteArgs): Promise<TemplateWebMCPToolResult<Record<string, unknown>>> {
  const invocationId = createInvocationId();
  const startedAt = Date.now();
  const parsed = parseReadInput(input);
  const readType = 'readType' in parsed ? parsed.readType : undefined;
  logTemplateWebMCP('invocation_start', {
    tool: TEMPLATE_BUILDER_READ_TOOL.name,
    invocationId,
    readType,
  });

  const finish = (
    result: TemplateWebMCPToolResult<Record<string, unknown>>,
    outcome: 'success' | 'validation_failure' | 'cancellation' | 'unexpected_failure',
  ) => {
    logTemplateWebMCP('invocation_complete', {
      tool: TEMPLATE_BUILDER_READ_TOOL.name,
      invocationId,
      readType,
      outcome,
      durationMs: Date.now() - startedAt,
      workingRevision: result.structuredContent.ok
        ? result.structuredContent.data.workingRevision
        : undefined,
    });
    return result;
  };

  try {
    if (signal?.aborted) {
      return finish(
        mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
        'cancellation',
      );
    }

    if (!canEdit) {
      return finish(
        mcpToolResult(toolError('UNAUTHORIZED', 'Template Builder edit permission is required.')),
        'validation_failure',
      );
    }

    if ('error' in parsed) {
      return finish(mcpToolResult(toolError('INVALID_INPUT', parsed.error)), 'validation_failure');
    }

    const state = store?.getState() as
      | (Record<string, unknown> & { name?: unknown; elements?: unknown })
      | null
      | undefined;
    const summary = sanitizeEditorSummary(state, templateId);
    if (!store || !state || !summary) {
      return finish(
        mcpToolResult(toolError('EDITOR_NOT_READY', 'The Template Builder editor is not ready.')),
        'validation_failure',
      );
    }

    void _persist;
    const documentData = { ...summary, readType: 'document' };

    if (parsed.readType === 'document') {
      return finish(mcpToolResult(toolSuccess(documentData)), 'success');
    }

    if (parsed.readType === 'selection' || parsed.readType === 'element') {
      const requestedId =
        typeof parsed.args.elementId === 'string'
          ? parsed.args.elementId
          : summary.selectedElementId;
      const element = summary.elements.find((item) => item.id === requestedId) ?? null;
      return finish(
        mcpToolResult(
          toolSuccess({
            readType: parsed.readType,
            workingRevision: summary.workingRevision,
            selectedElementId: summary.selectedElementId,
            element: element ? elementSvg(state, element) : null,
          }),
        ),
        'success',
      );
    }

    if (parsed.readType === 'advertisers') {
      if (routeMode === 'edit') {
        const id = typeof state.businessEntityId === 'number' ? state.businessEntityId : null;
        return finish(
          mcpToolResult(
            toolSuccess({
              readType: 'advertisers',
              workingRevision: summary.workingRevision,
              advertisers: id == null ? [] : [{ id }],
            }),
          ),
          'success',
        );
      }
      if (!listAdvertisers) {
        return finish(
          mcpToolResult(toolError('EDITOR_NOT_READY', 'The advertiser list is not available.')),
          'validation_failure',
        );
      }
      const advertisers = await listAdvertisers();
      return finish(
        mcpToolResult(
          toolSuccess({
            readType: 'advertisers',
            workingRevision: summary.workingRevision,
            advertisers: advertisers.map((advertiser) => ({
              id: advertiser._id ?? null,
              name: advertiser.name ?? '',
              type: advertiser.type ?? null,
            })),
          }),
        ),
        'success',
      );
    }

    const catalogs = await import('../liveCatalog');
    const catalogState = state as CatalogState;
    if (parsed.readType === 'source_options') {
      return finish(
        mcpToolResult(
          toolSuccess({
            readType: 'source_options',
            workingRevision: summary.workingRevision,
            sources: catalogs.sourceOptionsForState(catalogState),
          }),
        ),
        'success',
      );
    }
    if (parsed.readType === 'market_options') {
      return finish(
        mcpToolResult(
          toolSuccess({
            readType: 'market_options',
            workingRevision: summary.workingRevision,
            ...catalogs.marketOptionsForState(catalogState),
          }),
        ),
        'success',
      );
    }
    if (parsed.readType === 'data_points') {
      const elementId = typeof parsed.args.elementId === 'string' ? parsed.args.elementId : null;
      const fromElement = elementId
        ? summary.elements.find((element) => element.id === elementId)?.type
        : undefined;
      const elementType =
        typeof parsed.args.elementType === 'string' ? parsed.args.elementType : fromElement;
      if (!elementType) {
        return finish(
          mcpToolResult(
            toolError('INVALID_INPUT', 'data_points requires elementType or elementId.'),
          ),
          'validation_failure',
        );
      }
      return finish(
        mcpToolResult(
          toolSuccess({
            readType: 'data_points',
            workingRevision: summary.workingRevision,
            elementType,
            dataPoints: catalogs.dataPointsForState(catalogState, elementType),
          }),
        ),
        'success',
      );
    }
    if (parsed.readType === 'brand') {
      return finish(
        mcpToolResult(
          toolSuccess({
            readType: 'brand',
            workingRevision: summary.workingRevision,
            ...catalogs.brandSummary(catalogState),
          }),
        ),
        'success',
      );
    }
    if (parsed.readType === 'preview') {
      if (state.importSourceType === 'creatomate' && state.importSourceJSON) {
        return finish(
          mcpToolResult(
            toolError(
              'PREVIEW_FAILED',
              'Preview capture is not supported for Creatomate templates.',
            ),
          ),
          'validation_failure',
        );
      }
      const previewArgs = parsePreviewArgs(parsed.args);
      if ('error' in previewArgs) {
        return finish(
          mcpToolResult(toolError('INVALID_INPUT', previewArgs.error)),
          'validation_failure',
        );
      }
      try {
        const image = await withPreviewCaptureDeadline(
          (captureSignal) =>
            captureTemplatePreview({
              format: previewArgs.format,
              quality: previewArgs.quality,
              signal: captureSignal,
            }),
          signal,
        );
        return finish(
          mcpToolResult(
            toolSuccess({
              readType: 'preview',
              workingRevision: summary.workingRevision,
              width: image.width,
              height: image.height,
              format: previewArgs.format,
            }),
            { base64: image.base64, mimeType: image.mimeType },
          ),
          'success',
        );
      } catch (error) {
        if (signal?.aborted || (error instanceof DOMException && error.name === 'AbortError')) {
          return finish(
            mcpToolResult(toolError('UNEXPECTED', 'The tool invocation was cancelled.')),
            'cancellation',
          );
        }
        const timedOut = error instanceof PreviewCaptureTimeoutError;
        return finish(
          mcpToolResult(
            toolError(
              'PREVIEW_FAILED',
              timedOut ? error.message : 'Template Builder preview could not be captured.',
              timedOut
                ? undefined
                : { message: error instanceof Error ? error.message : String(error) },
            ),
          ),
          'validation_failure',
        );
      }
    }
    return finish(
      mcpToolResult(toolError('INVALID_INPUT', 'Unknown readType.')),
      'validation_failure',
    );
  } catch (error) {
    return finish(
      mcpToolResult(
        toolError('UNEXPECTED', 'An unexpected error occurred.', {
          message: error instanceof Error ? error.message : String(error),
        }),
      ),
      'unexpected_failure',
    );
  }
}
